import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts"
import type { IncidentTypeData, SecurityOperationData } from "../types/analytics.types"
import { ChartEmptyOverlay } from "../../../components/ChartEmptyOverlay"

interface IncidentTypesChartProps {
  data?: IncidentTypeData[]
  emptyTitle?: string
  emptyMessage?: string
}

export function IncidentTypesChart({ data, emptyTitle, emptyMessage }: IncidentTypesChartProps): JSX.Element {
  const isEmpty = !data || data.length === 0

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
      <h3 className="mb-4 text-base font-semibold text-gray-900">Most Frequent Incident Types</h3>
      {isEmpty ? (
        <ChartEmptyOverlay title={emptyTitle} message={emptyMessage} heightClassName="h-52" />
      ) : (
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-6">
          <div className="h-44 w-44 shrink-0 sm:h-52 sm:w-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  animationBegin={0}
                  animationDuration={800}
                >
                  {data.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto">
            {data.map((item) => (
              <div key={item.name} className="flex items-center gap-2 text-sm">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-gray-700">{item.name}</span>
                <span className="font-semibold text-gray-900">{item.value}</span>
                <span className="text-gray-500">{item.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

interface SecurityOperationsChartProps {
  data?: SecurityOperationData[]
  emptyTitle?: string
  emptyMessage?: string
}

export function SecurityOperationsChart({ data, emptyTitle, emptyMessage }: SecurityOperationsChartProps): JSX.Element {
  const isEmpty = !data || data.length === 0

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
      <h3 className="mb-4 text-base font-semibold text-gray-900">Security Operation By Unit</h3>
      {isEmpty ? (
        <ChartEmptyOverlay title={emptyTitle} message={emptyMessage} heightClassName="h-52" />
      ) : (
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-6">
          <div className="h-44 w-44 shrink-0 sm:h-52 sm:w-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="unit"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  animationBegin={0}
                  animationDuration={800}
                >
                  {data.map((entry) => (
                    <Cell key={entry.unit} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex w-full flex-wrap gap-x-4 gap-y-2 sm:w-auto">
            {data.map((item) => (
              <div key={item.unit} className="flex items-center gap-2 text-sm">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-gray-700">{item.unit}</span>
                <span className="font-semibold text-gray-900">{item.value}</span>
                <span className="text-gray-500">{item.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
